import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";

/**
 * Shows daily free-tier usage (10 MCQs + 3 AI tutor uses) with an Upgrade CTA.
 * Hidden for Pro/trial users.
 */
const DailyUsageBar = () => {
  const navigate = useNavigate();
  const { isPro, user } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!user || isPro) return;
    api.get("/usage/today").then(setUsage).catch(() => {});
  }, [user, isPro]);

  if (isPro || !usage || usage.limit == null) return null;

  const pct = Math.min(100, (usage.used / usage.limit) * 100);
  const lowRemaining = usage.remaining <= 3;

  return (
    <div
      data-testid="daily-usage-bar"
      className="glass rounded-2xl p-4 border border-border flex items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-muted-foreground">
            Free questions today - {" "}
            <span className={lowRemaining ? "text-warning font-medium" : "text-foreground"}>
              {usage.used}/{usage.limit}
            </span>
          </p>
          {lowRemaining && (
            <span className="text-[10px] uppercase tracking-wider text-warning">
              {usage.remaining} left
            </span>
          )}
        </div>
        <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              lowRemaining ? "bg-warning" : "bg-primary"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {usage.ai_limit != null && (
          <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="daily-ai-usage">
            AI tutor today: {usage.ai_used}/{usage.ai_limit}
          </p>
        )}
      </div>
      <button
        onClick={() => navigate("/billing")}
        data-testid="daily-usage-upgrade-btn"
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary/15 hover:bg-primary/25 text-primary text-xs font-medium border border-primary/30 transition-all"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Go unlimited
      </button>
    </div>
  );
};

export default DailyUsageBar;
